import _isPlainObject from "lodash.isplainobject";
import { stepTypes } from "./constants";

const DEFAULT_ROUTE = "/";

export const reviewStepModel = {
  id: "kontrollera-uppgifter",
  type: stepTypes.REVIEW,
  title: "step.review.label",
  isSubmitted: false
};

/**
 * Returns a route matching all stepIds supplied
 * @param ids
 * @returns {string}
 */
export const createRouteFromStepIds = ids => `/(${ids.join("|")})`;

/**
 * Formats a step url
 * @param id
 * @returns {string}
 */
export const formatUrl = id => `/${id}`;

/**
 * Is the current step active
 * @param url
 * @param stepId
 * @returns {boolean}
 */
export const isStepActiveById = (url, stepId) => formatUrl(stepId) === url;

/**
 * Is the stepId allowed looking at the allowed route
 * @param allowedRoutes
 * @param stepId
 * @returns {*}
 */
export const getIsStepAllowedByStepId = (allowedRoutes, stepId) => {
  return allowedRoutes.includes(stepId);
};

/**
 * Add default props as well as review step model
 * @param steps
 * @returns {*[]}
 */
export const mapSteps = steps => {
  return [
    ...steps.map(step => ({
      ...step,
      type: stepTypes.FORM,
      isSubmitted: false
    })),
    reviewStepModel
  ];
};

/**
 * Based on the current state, find out what url to redirect to if a forbidden route is visited
 * @param hasSteps
 * @param firstUnsubmittedStep
 * @returns {string}
 */
export const getRedirectUrl = (hasSteps, firstUnsubmittedStep) => {
  return hasSteps && firstUnsubmittedStep
    ? formatUrl(firstUnsubmittedStep.id)
    : DEFAULT_ROUTE;
};

/**
 * Based on te current state, find out what routes are currently allowed
 * @param isPristine
 * @param firstUnsubmittedStep
 * @param isSubmitting
 * @param stepsUntilFirstUnsubmitted
 * @returns {string}
 */
export const calculateAllowedRoutes = (
  isPristine,
  firstUnsubmittedStep,
  isSubmitting,
  stepsUntilFirstUnsubmitted
) => {
  if (isSubmitting) {
    // Only allow review step
    return createRouteFromStepIds([reviewStepModel.id]);
  } else if (isPristine) {
    return createRouteFromStepIds([firstUnsubmittedStep.id]);
  } else {
    const ids = stepsUntilFirstUnsubmitted.map(step => step.id);
    return createRouteFromStepIds(ids);
  }
};

/**
 * Make sure any undefined values are replace with the supplied value
 * Uses JSON.stringify internally so it's not particularly performant
 * @param data
 * @param replacement
 * @returns {string}
 */
export const deepReplaceUndefined = (data, replacement = "") =>
  JSON.parse(
    JSON.stringify(data, (key, value) =>
      typeof value === "undefined" ? replacement : value
    )
  );

/**
 * Format data to pass to the application as the complete flow data
 * @param steps
 * @returns {*}
 */
export const formatFlowFormData = steps => {
  return deepReplaceUndefined(
    steps.map(({ title, renderedFormData: answers }, index) => ({
      index,
      title,
      answers
    }))
  );
};

/**
 * Format the steps in a way that is suitable for the review step to handle
 * @param formSteps
 * @returns {*}
 */
export const formatReviewStepData = formSteps => {
  return formSteps.reduce((acc, step, index) => {
    acc[step.id] = {
      index: index,
      title: step.title,
      formData: removeHiddenGroupObjects(step.renderedFormData)
    };
    return acc;
  }, {});
};

export const removeHiddenGroupObjects = data => {
  const hiddenGroupId = RegExp("^hidden-");
  if (_isPlainObject(data)) {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (hiddenGroupId.test(key)) {
        acc = { ...acc, ...removeHiddenGroupObjects(value) };
        return acc;
      }
      acc[key] = removeHiddenGroupObjects(value);
      return acc;
    }, {});
  }
  if (Array.isArray(data)) {
    return data.map(removeHiddenGroupObjects);
  }
  return data;
};
